import React from 'react';
import { Image as ImageIcon, Plus, X } from 'lucide-react';

export interface InclusionApp {
  name: string;
  image?: string;
}

interface InclusionAppsEditorProps {
  /** Current list of apps shown as an icon grid in the customer plan-detail modal. */
  value?: InclusionApp[];
  onChange: (next: InclusionApp[]) => void;
  /** Opens the shared image picker. The chosen URL is passed to `apply`. */
  onPickIcon?: (apply: (url: string) => void) => void;
  getCachedImageUrl?: (url: string | undefined) => string | undefined;
  /** Maximum number of apps (default 12, matches customer renderer). */
  max?: number;
}

/**
 * Editor for `inclusionApps` — currently surfaced for Microsoft software products
 * (M365 plans). Renders a grid of name + icon pairs that the customer modal
 * displays as the "Apps included" tile grid.
 */
export function InclusionAppsEditor({
  value = [],
  onChange,
  onPickIcon,
  getCachedImageUrl,
  max = 12,
}: InclusionAppsEditorProps) {
  const items = value;

  const addRow = () => {
    if (items.length >= max) return;
    onChange([...items, { name: '' }]);
  };

  const removeRow = (i: number) =>
    onChange(items.filter((_, idx) => idx !== i));

  const patchRow = (i: number, patch: Partial<InclusionApp>) =>
    onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-gradient-to-br from-blue-50/50 to-indigo-50/50">
      <div className="flex items-center justify-between mb-2">
        <div>
          <label className="block text-xs font-medium text-gray-700">Apps Included</label>
          <p className="text-xs text-gray-500">Shown as an icon grid on the customer plan detail (e.g. Word, Excel, Teams).</p>
        </div>
        <button
          type="button"
          onClick={addRow}
          disabled={items.length >= max}
          className={`px-2 py-1 rounded text-xs font-medium transition-colors inline-flex items-center gap-1 ${
            items.length >= max
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          <Plus className="w-3 h-3" />
          Add app
        </button>
      </div>

      {items.length === 0 ? (
        <div className="text-xs text-gray-400 text-center py-4 border border-dashed border-gray-300 rounded bg-white/60">
          No apps yet. Click "Add app" to add icons (Microsoft Office apps, etc.).
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {items.map((item, idx) => {
            const iconUrl = getCachedImageUrl ? getCachedImageUrl(item.image) : item.image;
            return (
              <div key={idx} className="bg-white rounded border border-gray-200 p-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onPickIcon?.((url) => patchRow(idx, { image: url }))}
                  disabled={!onPickIcon}
                  className={`w-10 h-10 flex-shrink-0 rounded-lg border bg-gray-50 flex items-center justify-center overflow-hidden transition-colors ${
                    onPickIcon ? 'border-gray-200 hover:border-blue-400 hover:bg-blue-50 cursor-pointer' : 'border-gray-200 cursor-default opacity-60'
                  }`}
                  title={onPickIcon ? 'Choose icon' : 'Icon picker unavailable'}
                >
                  {iconUrl ? (
                    <img src={iconUrl} alt="" className="w-8 h-8 object-contain" />
                  ) : (
                    <ImageIcon className="w-4 h-4 text-gray-300" />
                  )}
                </button>

                <input
                  type="text"
                  value={item.name}
                  onChange={(e) => patchRow(idx, { name: e.target.value })}
                  placeholder="App name"
                  className="flex-1 min-w-0 rounded border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />

                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                  title="Remove app"
                  aria-label="Remove app"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
      {items.length >= max && (
        <p className="text-[11px] text-gray-400 mt-1">Maximum of {max} apps reached.</p>
      )}
    </div>
  );
}
